import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ArrowLeft, FileText, LucideAngularModule } from 'lucide-angular';
import { VENDOR_TYPES } from '../../vendor/pages/vendor.constants';
import { ToastService } from '../../../shared/ui/toast.service';
import { VendorDocumentsService } from '../services/vendor-documents.service';

const MODULE_OPTIONS = ['Vendor Module', 'LOA Module', 'Agreement Module', 'Billing Module'];
const FILE_TYPE_OPTIONS = ['PDF', 'JPG', 'PNG', 'DOC', 'DOCX', 'XLS', 'XLSX'];
const STATUS_OPTIONS = ['Active', 'Inactive'];

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  styleUrl: './vendor-document-config-form.page.scss',
  template: `
    <div class="vendor-page">
      <div class="p-3 p-lg-4 vendor-shell">
        <div class="d-flex flex-column flex-xl-row justify-content-between gap-3 align-items-xl-center">
          <div class="d-flex align-items-center gap-3">
            <span class="title-badge">
              <lucide-icon [img]="FileTextIcon" [size]="18"></lucide-icon>
            </span>
            <div>
              <div class="vendor-page-header__eyebrow">Document Configuration</div>
              <h1 class="page-title-lite">{{ isEdit() ? 'Edit Configuration' : 'Add New Configuration' }}</h1>
              <p class="vendor-page-subtitle mb-0">Define module-specific document rules, supported file types, and client mappings.</p>
            </div>
          </div>

          <button class="btn btn-sm create-btn export" type="button" (click)="router.navigateByUrl('/document-configurations')">
            <lucide-icon [img]="ArrowLeftIcon" [size]="14"></lucide-icon><span class="ms-2">BACK</span>
          </button>
        </div>
      </div>

      <form [formGroup]="form" class="d-grid gap-3" (ngSubmit)="submit()">
        <div class="section-card">
          <div class="section-header">Configuration Details</div>
          <div class="section-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Module Name *</label>
                <select class="form-select" formControlName="module_name">
                  <option value="" disabled>Select Module</option>
                  @for (item of moduleOptions; track item) {
                    <option [value]="item">{{ item }}</option>
                  }
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Document Name *</label>
                <input class="form-control" formControlName="doc_title" placeholder="Enter document name" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Maximum File Size (MB) *</label>
                <input class="form-control" type="number" min="1" step="1" formControlName="max_file_size_mb" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Status *</label>
                <select class="form-select" formControlName="status">
                  @for (item of statusOptions; track item) {
                    <option [value]="item">{{ item }}</option>
                  }
                </select>
              </div>

              <div class="col-md-6 d-flex align-items-end">
                <div class="toggle-card w-100">
                  <input class="form-check-input" type="checkbox" id="mandatoryDoc" formControlName="mandatory" />
                  <label class="form-check-label ms-2" for="mandatoryDoc">Mandatory document</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-lg-6">
            <div class="section-card h-100">
              <div class="section-header">Client Types</div>
              <div class="section-body">
                <label class="form-label">Client Type *</label>
                <input
                  class="form-control mb-3"
                  placeholder="Search client types"
                  [value]="clientTypeSearch()"
                  (input)="clientTypeSearch.set(($any($event.target).value || '').toString())"
                />
                <div class="selection-panel">
                  @for (item of filteredClientTypes(); track item) {
                    <label class="selection-option">
                      <input class="form-check-input" type="checkbox" [checked]="selectedClientTypes().includes(item)" (change)="toggleClientType(item)" />
                      <span>{{ item }}</span>
                    </label>
                  }
                </div>
                @if (selectedClientTypes().length > 0) {
                  <div class="chip-list">
                    @for (item of selectedClientTypes(); track item) {
                      <span class="selection-chip selection-chip--primary">
                        {{ item }}
                        <button type="button" class="chip-close" (click)="removeClientType(item)">x</button>
                      </span>
                    }
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="section-card h-100">
              <div class="section-header">Allowed File Types</div>
              <div class="section-body">
                <label class="form-label">Allowed File Types *</label>
                <input
                  class="form-control mb-3"
                  placeholder="Search file types"
                  [value]="fileTypeSearch()"
                  (input)="fileTypeSearch.set(($any($event.target).value || '').toString())"
                />
                <div class="selection-panel">
                  @for (item of filteredFileTypes(); track item) {
                    <label class="selection-option">
                      <input class="form-check-input" type="checkbox" [checked]="selectedFileTypes().includes(item)" (change)="toggleFileType(item)" />
                      <span>{{ item }}</span>
                    </label>
                  }
                </div>
                @if (selectedFileTypes().length > 0) {
                  <div class="chip-list">
                    @for (item of selectedFileTypes(); track item) {
                      <span class="selection-chip selection-chip--muted">
                        {{ item }}
                        <button type="button" class="chip-close" (click)="removeFileType(item)">x</button>
                      </span>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-body d-flex justify-content-end gap-2">
            <button class="btn btn-sm create-btn export" type="button" (click)="router.navigateByUrl('/document-configurations')">CANCEL</button>
            <button class="btn btn-sm create-btn primary" type="submit" [disabled]="loading()">
              {{ loading() ? 'SAVING...' : (isEdit() ? 'UPDATE' : 'SAVE') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  `,
})
export class VendorDocumentConfigFormPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly documentsService = inject(VendorDocumentsService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);

  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly FileTextIcon = FileText;
  protected readonly moduleOptions = MODULE_OPTIONS;
  protected readonly clientTypeOptions = VENDOR_TYPES;
  protected readonly statusOptions = STATUS_OPTIONS;

  protected readonly isEdit = signal(false);
  protected readonly loading = signal(false);
  protected readonly selectedFileTypes = signal<string[]>(['PDF']);
  protected readonly selectedClientTypes = signal<string[]>([]);
  protected readonly clientTypeSearch = signal('');
  protected readonly fileTypeSearch = signal('');

  protected readonly filteredClientTypes = computed(() => {
    const q = this.clientTypeSearch().trim().toLowerCase();
    return !q ? this.clientTypeOptions : this.clientTypeOptions.filter((item) => item.toLowerCase().includes(q));
  });

  protected readonly filteredFileTypes = computed(() => {
    const q = this.fileTypeSearch().trim().toLowerCase();
    return !q ? FILE_TYPE_OPTIONS : FILE_TYPE_OPTIONS.filter((item) => item.toLowerCase().includes(q));
  });

  private editId: string | null = null;

  protected readonly form = this.fb.nonNullable.group({
    module_name: ['', Validators.required],
    doc_title: ['', Validators.required],
    max_file_size_mb: [10, [Validators.required, Validators.min(1)]],
    mandatory: [false],
    status: ['Active', Validators.required],
  });

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.editId = id;
      this.isEdit.set(!!id);
      if (!id) {
        this.selectedFileTypes.set(['PDF']);
        this.selectedClientTypes.set([]);
        this.form.reset({ module_name: '', doc_title: '', max_file_size_mb: 10, mandatory: false, status: 'Active' });
        return;
      }

      this.loading.set(true);
      this.documentsService.getConfig(id).subscribe({
        next: (config) => {
          const allowed = (config.allowed_file_types || config.file_type || 'PDF')
            .split(',')
            .map((item) => item.trim().toUpperCase())
            .filter(Boolean);

          const mappedClientTypes = (config.client_types || config.client_type || '')
            .split(',')
            .map((item) => item.trim())
            .map((item) => this.clientTypeOptions.find((option) => option.toLowerCase() === item.toLowerCase()) || item)
            .filter((item) => !!item && this.clientTypeOptions.includes(item));

          this.form.reset({
            module_name: config.module_name || '',
            doc_title: config.doc_title,
            max_file_size_mb: config.max_file_size_mb || 10,
            mandatory: !!config.mandatory,
            status: config.status || 'Active',
          });
          this.selectedFileTypes.set(allowed.length ? allowed : ['PDF']);
          this.selectedClientTypes.set(mappedClientTypes);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toast.show('Document config not found', '', 'danger');
          this.router.navigateByUrl('/document-configurations');
        },
      });
    });
  }

  protected toggleClientType(item: string): void {
    const current = this.selectedClientTypes();
    this.selectedClientTypes.set(current.includes(item) ? current.filter((v) => v !== item) : [...current, item]);
  }

  protected removeClientType(item: string): void {
    this.selectedClientTypes.set(this.selectedClientTypes().filter((v) => v !== item));
  }

  protected toggleFileType(item: string): void {
    const current = this.selectedFileTypes();
    this.selectedFileTypes.set(current.includes(item) ? current.filter((v) => v !== item) : [...current, item]);
  }

  protected removeFileType(item: string): void {
    this.selectedFileTypes.set(this.selectedFileTypes().filter((v) => v !== item));
  }

  protected submit(): void {
    const docTitle = this.form.controls.doc_title.value.trim();
    if (!docTitle) {
      this.toast.show('Validation error', 'Document Name is required.', 'danger');
      return;
    }

    const moduleName = this.form.controls.module_name.value;
    if (!moduleName) {
      this.toast.show('Validation error', 'Module Name is required.', 'danger');
      return;
    }

    const selectedClients = this.selectedClientTypes();
    if (selectedClients.length === 0) {
      this.toast.show('Validation error', 'Select at least one Client Type.', 'danger');
      return;
    }

    const allowedTypes = this.selectedFileTypes();
    if (allowedTypes.length === 0) {
      this.toast.show('Validation error', 'Select at least one Allowed File Type.', 'danger');
      return;
    }

    const maxSize = Number(this.form.controls.max_file_size_mb.value || 0);
    if (!Number.isFinite(maxSize) || maxSize <= 0) {
      this.toast.show('Validation error', 'Maximum File Size must be greater than 0.', 'danger');
      return;
    }

    this.loading.set(true);

    const payload = {
      module_name: moduleName,
      client_type: selectedClients[0],
      client_types: selectedClients.join(', '),
      doc_title: docTitle,
      allowed_file_types: allowedTypes.join(', '),
      file_type: allowedTypes.join(', '),
      max_file_size_mb: maxSize,
      mandatory: !!this.form.controls.mandatory.value,
      status: this.form.controls.status.value || 'Active',
      effective_date: null,
    };

    if (this.isEdit() && this.editId) {
      this.documentsService.updateConfig(this.editId, payload).subscribe({
        next: () => {
          this.loading.set(false);
          this.toast.show('Document config updated', '', 'success');
          this.router.navigateByUrl('/document-configurations');
        },
        error: () => {
          this.loading.set(false);
          this.toast.show('Failed to update config', '', 'danger');
        },
      });
      return;
    }

    this.documentsService.createConfig(payload).subscribe({
      next: () => {
        this.loading.set(false);
        this.toast.show('Document config created', '', 'success');
        this.router.navigateByUrl('/document-configurations');
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to create config', '', 'danger');
      },
    });
  }
}
